import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import {
  registrationNumber as registrationNumberValidator,
  vatNumber as vatNumberValidator,
  url as urlValidator,
} from '@klarna/mf-validations';
import { BorderlessDropdown } from '@klarna/bubble-ui';
import { formUtils, Input, prependHttpScheme } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { validators } from '@/flow/validation';
import { IntegerInput } from '@/components';
import { CY_PRE_ASSESSMENT_COMPANY_DETAILS_CONTAINER_IDS } from './constants';
import SwitchCheckboxWrapper from '../PreAssessmentSmeCompanyDetails/SwitchCheckboxWrapper';

const {
  INPUT_BUSINESS_CATEGORY,
  INPUT_COMPANY_NAME,
  INPUT_IS_MANAGED,
  INPUT_OPERATING_COUNTRY,
  INPUT_REGISTRATION_AUTHORITY,
  INPUT_REGISTRATION_COUNTRY,
  INPUT_REGISTRATION_NUMBER,
  INPUT_STORE_URL,
  INPUT_VAT_ID,
  VOLUME_BY_CARD,
  AVERAGE_ORDER_VALUE,
} = CY_PRE_ASSESSMENT_COMPANY_DETAILS_CONTAINER_IDS;

const tErrorsPrefix = 'products-fe.error_messages.validation';

export function prepareFields(props, tPrefix, currentFields) {
  const {
    countries = [],
    defaultState = {},
    goodsCategories = [],
    registrationCourts = [],
    isSME = false,
    forcedRegistrationIdCountries = [],
    t,
    forcedValidationConfig = {},
  } = props;

  const isManaged = _get(currentFields, 'isManaged.value', _get(defaultState, 'isManaged', false));

  const volumeByCard = _get(
    currentFields,
    'volumeByCard.value',
    _get(defaultState, 'volumeByCard', ''),
  );

  const averageOrderValue = _get(
    currentFields,
    'averageOrderValue.value',
    _get(defaultState, 'averageOrderValue', ''),
  );

  const registrationCountry = _get(
    currentFields,
    'registrationCountry.value',
    _get(defaultState, 'registrationCountry', ''),
  );
  const operatingCountry =
    _get(currentFields, 'operatingCountry.value', _get(defaultState, 'operatingCountry', '')) ||
    registrationCountry;

  const registrationNumber = _get(
    currentFields,
    'registrationNumber.value',
    _get(defaultState, 'registrationNumber', ''),
  );
  const registrationAuthority = _get(
    currentFields,
    'registrationAuthority.value',
    _get(defaultState, 'registrationAuthority', ''),
  );
  const companyName = _get(
    currentFields,
    'companyName.value',
    _get(defaultState, 'companyName', ''),
  );
  const storeUrl = _get(currentFields, 'storeUrl.value', _get(defaultState, 'storeUrl', ''));
  const goodsCategory = _get(
    currentFields,
    'goodsCategory.value',
    _get(defaultState, 'goodsCategory', ''),
  );
  const vatNumber = _get(currentFields, 'vatNumber.value', _get(defaultState, 'vatNumber', ''));

  const tPrefixFields = `${tPrefix}.fields`;

  const getFieldValidationConfig = field => forcedValidationConfig[field] || {};

  const tField = (tKey, field) =>
    t(tKey, getFieldValidationConfig(field).translationKey || registrationCountry);

  const fields = {
    volumeByCard: {
      component: IntegerInput,
      size: 1,
      label: t(`${tPrefix}.fields.volume_by_card.label`),
      value: volumeByCard !== null ? volumeByCard : '',
      isInitialized: false,
      isValid: false,
      validators: [
        {
          callback: validators.common.isInteger({ min: 0, max: 100 }),
          errorMessage: t(`${tPrefix}.fields.volume_by_card.errors.invalid`),
        },
      ],
      extraProps: {
        tooltipText: t(`${tPrefix}.fields.volume_by_card.errors.invalid_hint`),
        ...generateCyElemId(VOLUME_BY_CARD, window),
      },
    },
    ...(!isSME &&
      averageOrderValue && {
        averageOrderValue: {
          component: IntegerInput,
          size: 1,
          label: t(`${tPrefix}.fields.average_order_value.label`),
          value: averageOrderValue !== null ? averageOrderValue : '',
          isInitialized: false,
          isValid: false,
          validators: [
            {
              callback: validators.common.isInteger({ min: 0 }),
              errorMessage: t(`${tPrefix}.fields.average_order_value.errors.invalid`),
            },
          ],
          extraProps: {
            disabled: true,
            tooltipText: t(`${tPrefix}.fields.average_order_value.tooltip`),
            ...generateCyElemId(AVERAGE_ORDER_VALUE, window),
          },
        },
      }),
    companyName: {
      component: Input,
      size: 1,
      type: 'text',
      label: t(`${tPrefixFields}.company_name.label`),
      value: companyName,
      validators: [
        {
          callback: validators.common.isRequired,
          errorMessage: t(`${tPrefixFields}.company_name.errors.required`),
        },
      ],
      isInitialized: false,
      isValid: false,
      extraProps: {
        ...generateCyElemId(INPUT_COMPANY_NAME, window),
      },
    },
    registrationCountry: {
      component: BorderlessDropdown,
      size: 1,
      label: t(`${tPrefixFields}.registration_country.label`),
      value: registrationCountry,
      validators: [
        {
          callback: validators.common.isRequired,
          errorMessage: t(`${tPrefixFields}.registration_country.errors.required`),
        },
      ],
      extraProps: {
        disabled: true,
        autoFocus: false,
        options: countries,
        ...generateCyElemId(INPUT_REGISTRATION_COUNTRY, window),
      },
      isInitialized: false,
      isValid: false,
    },
    registrationNumber: {
      component: Input,
      size: 1,
      type: 'text',
      label: tField(`${tPrefixFields}.registration_number.label`, 'registrationId'),
      value: registrationNumber,
      validators: [
        {
          callback:
            getFieldValidationConfig('registrationId').validate ||
            registrationNumberValidator.validate,
          errorMessage: tField(`${tErrorsPrefix}.registrationId.general`, 'registrationId'),
          options: {
            country: registrationCountry,
            optional:
              !forcedRegistrationIdCountries.includes(registrationCountry) &&
              !registrationAuthority,
          },
        },
      ],
      extraProps: {
        tooltipText: tField(`${tErrorsPrefix}.registrationId.invalid`, 'registrationId'),
        ...generateCyElemId(INPUT_REGISTRATION_NUMBER, window),
      },
      isInitialized: false,
      isValid: false,
    },
    ...(registrationCountry === 'AU' && {
      vatNumber: {
        component: Input,
        size: 1,
        type: 'text',
        label: t(`${tPrefixFields}.vat_number.label`),
        value: vatNumber,
        validators: [
          {
            callback: val =>
              _isEmpty(registrationNumber) ? validators.common.isRequired(val) : true,
            errorMessage: t(`${tPrefixFields}.vat_number.errors.required`),
          },
          {
            callback: (val, options) =>
              _isEmpty(registrationNumber) || !_isEmpty(val)
                ? vatNumberValidator.validate(val, options)
                : true,
            errorMessage: t(`${tPrefixFields}.vat_number.errors.invalid`),
            options: { country: registrationCountry },
          },
        ],
        isInitialized: false,
        isValid: false,
        extraProps: {
          ...generateCyElemId(INPUT_VAT_ID, window),
        },
      },
    }),
    ...(registrationCountry === 'DE' && {
      registrationAuthority: {
        component: BorderlessDropdown,
        size: 1,
        type: 'text',
        label: t(`${tPrefixFields}.registration_authority.label`),
        value: registrationCountry === 'DE' && registrationAuthority,
        validators: [
          {
            callback: val => (registrationNumber ? validators.common.isRequired(val) : true),
            errorMessage: t(`${tPrefixFields}.registration_authority.errors.required`),
          },
        ],
        extraProps: {
          options: registrationCourts,
          ...generateCyElemId(INPUT_REGISTRATION_AUTHORITY, window),
        },
        isInitialized: false,
        isValid: false,
      },
    }),
    operatingCountry: {
      component: BorderlessDropdown,
      size: 1,
      label: t(`${tPrefixFields}.operating_country.label`),
      value: operatingCountry || '',
      validators: [
        {
          callback: validators.common.isRequired,
          errorMessage: t(`${tPrefixFields}.operating_country.errors.required`),
        },
      ],
      extraProps: {
        options: countries,
        ...generateCyElemId(INPUT_OPERATING_COUNTRY, window),
      },
      isInitialized: false,
      isValid: false,
    },
    storeUrl: {
      component: Input,
      size: 1,
      type: 'text',
      label: t(`${tPrefixFields}.store_url.label`),
      value: storeUrl,
      validators: [
        {
          callback: validators.common.isRequired,
          errorMessage: t(`${tPrefixFields}.store_url.errors.required`),
        },
        {
          callback: urlValidator.validate,
          errorMessage: t(`${tPrefixFields}.store_url.errors.invalid`),
        },
      ],
      isInitialized: false,
      isValid: false,
      extraProps: {
        disabled: true,
        ...generateCyElemId(INPUT_STORE_URL, window),
      },
    },
    goodsCategory: {
      component: BorderlessDropdown,
      size: 1,
      label: t(`${tPrefixFields}.goods_category.label`),
      value: goodsCategory,
      isInitialized: false,
      isValid: false,
      validators: [
        {
          callback: validators.common.isRequired,
          errorMessage: t(`${tPrefixFields}.goods_category.errors.invalid`),
        },
      ],
      extraProps: {
        disabled: true,
        options: goodsCategories,
        ...generateCyElemId(INPUT_BUSINESS_CATEGORY, window),
      },
    },
    isManaged: {
      component: SwitchCheckboxWrapper,
      size: 1,
      label: t(`${tPrefixFields}.is_managed.label`),
      value: isManaged,
      isInitialized: true,
      isValid: true,
      extraProps: {
        ...generateCyElemId(INPUT_IS_MANAGED, window),
      },
    },
  };

  return formUtils.initForm(fields);
}

export const getValues = fields => ({
  isManaged: fields.isManaged.value,
  volumeByCard: fields.volumeByCard.value,
  averageOrderValue: fields.averageOrderValue?.value || undefined,
  registrationCountry: fields.registrationCountry.value,
  operatingCountry: fields.operatingCountry.value,
  registrationNumber: fields.registrationNumber.value,
  vatNumber: _get(fields, 'vatNumber.value', null),
  registrationAuthority: _get(fields, 'registrationAuthority.value', null),
  companyName: fields.companyName.value,
  storeUrl: prependHttpScheme(fields.storeUrl.value),
  goodsCategory: fields.goodsCategory.value,
});
